import { useMemo, useRef, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import useWorldStore from '../store/worldStore';
import { getChatAppModelGlb } from '../lib/chatAppCharacters';
import SceneDebugOverlay from './SceneDebugOverlay';
import {
  ROOM_SHELL_CAMERA_CONFIG,
  ROOM_SHELL_STAGE_Z_OFFSET,
} from '../../../lib/world/room-shell-geometry';

// Walkable bounds (matches ff-test square layout)
const WALK_X = 3.8;
const WALK_Z = 2.5;
const WALK_INSET = 0.35;
const WALK_CENTER_Z = ROOM_SHELL_STAGE_Z_OFFSET;
const PLAYER_SPEED = 0.04;

function LocationCharacter({ modelGlb, startX = 0, agentId }) {
  const gltf = useGLTF(modelGlb);
  const mixerRef = useRef();
  const actionsRef = useRef({});
  const currentKeyRef = useRef(null);
  const groupRef = useRef();
  const walkTargetRef = useRef(null);
  const walkTimerRef = useRef(0);
  const isHoveredRef = useRef(false);
  const hoverPhaseRef = useRef(null); // null | 'wave' | 'idle'
  const hoverTimerRef = useRef(null);

  const clonedScene = useMemo(() => {
    const scene = skeletonClone(gltf.scene);
    scene.traverse((node) => {
      if (node.isMesh) {
        node.castShadow = true;
      }
    });
    return scene;
  }, [gltf.scene]);

  useEffect(() => {
    const mixer = new THREE.AnimationMixer(clonedScene);
    mixerRef.current = mixer;

    const idleClip = gltf.animations.find((c) => c.name === 'idle');
    const walkClip = gltf.animations.find((c) => c.name === 'walking');

    if (idleClip) {
      actionsRef.current.idle = mixer.clipAction(idleClip);
      actionsRef.current.idle.loop = THREE.LoopRepeat;
      actionsRef.current.idle.play();
      currentKeyRef.current = 'idle';
    }
    if (walkClip) {
      actionsRef.current.walk = mixer.clipAction(walkClip);
      actionsRef.current.walk.loop = THREE.LoopRepeat;
    }
    const waveClip = gltf.animations.find((c) => c.name === 'greeting');
    if (waveClip) {
      actionsRef.current.wave = mixer.clipAction(waveClip);
      actionsRef.current.wave.loop = THREE.LoopOnce;
      actionsRef.current.wave.clampWhenFinished = true;
    }

    // When wave finishes, transition to idle while still hovered
    mixer.addEventListener('finished', (e) => {
      if (e.action === actionsRef.current.wave && isHoveredRef.current) {
        hoverPhaseRef.current = 'idle';
        if (actionsRef.current.idle) {
          e.action.crossFadeTo(actionsRef.current.idle.reset().play(), 0.3, true);
          currentKeyRef.current = 'idle';
        }
      }
    });

    return () => mixer.stopAllAction();
  }, [clonedScene, gltf.animations]);

  useFrame((state, delta) => {
    mixerRef.current?.update(delta);
    if (!groupRef.current) return;

    // Determine target animation
    let targetAnim;
    if (isHoveredRef.current) {
      // Wave once on hover, then idle
      targetAnim = hoverPhaseRef.current === 'wave' ? 'wave' : 'idle';
      // Face camera when hovered
      const cam = state.camera.position;
      const pos = groupRef.current.position;
      groupRef.current.rotation.y = Math.atan2(cam.x - pos.x, cam.z - pos.z);
    } else {
      // Movement logic
      walkTimerRef.current -= delta;

      if (walkTimerRef.current <= 0) {
        if (walkTargetRef.current === null) {
          const bx = WALK_X - WALK_INSET;
          const bz = WALK_Z - WALK_INSET;
          walkTargetRef.current = {
            x: (Math.random() - 0.5) * 2 * bx,
            z: WALK_CENTER_Z + ((Math.random() - 0.5) * 2 * bz),
          };
          walkTimerRef.current = 3 + Math.random() * 4;
        } else {
          walkTargetRef.current = null;
          walkTimerRef.current = 2 + Math.random() * 5;
        }
      }

      const target = walkTargetRef.current;
      const pos = groupRef.current.position;

      if (target) {
        const dx = target.x - pos.x;
        const dz = target.z - pos.z;
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist > 0.1) {
          pos.x += (dx / dist) * PLAYER_SPEED;
          pos.z += (dz / dist) * PLAYER_SPEED;
          pos.x = Math.max(-WALK_X + WALK_INSET, Math.min(WALK_X - WALK_INSET, pos.x));
          pos.z = Math.max(
            WALK_CENTER_Z - WALK_Z + WALK_INSET,
            Math.min(WALK_CENTER_Z + WALK_Z - WALK_INSET, pos.z)
          );
          groupRef.current.rotation.y = Math.atan2(dx, dz);
          targetAnim = 'walk';
        } else {
          walkTargetRef.current = null;
          walkTimerRef.current = 2 + Math.random() * 4;
          targetAnim = 'idle';
        }
      } else {
        targetAnim = 'idle';
      }
    }

    // Crossfade animation
    if (targetAnim && targetAnim !== currentKeyRef.current && actionsRef.current[targetAnim]) {
      const current = actionsRef.current[currentKeyRef.current];
      const next = actionsRef.current[targetAnim];
      if (current) {
        current.crossFadeTo(next.reset().play(), 0.25, true);
      } else {
        next.reset().play();
      }
      currentKeyRef.current = targetAnim;
    }
  });

  return (
    <group ref={groupRef} position={[startX, 0, WALK_CENTER_Z]}
      onPointerEnter={(e) => {
        e.stopPropagation();
        isHoveredRef.current = true;
        hoverPhaseRef.current = 'wave';
        document.body.style.cursor = 'pointer';
      }}
      onPointerLeave={(e) => {
        e.stopPropagation();
        isHoveredRef.current = false;
        hoverPhaseRef.current = null;
        document.body.style.cursor = '';
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (agentId) {
          useWorldStore.getState().selectAgent(agentId);
        }
      }}
    >
      <primitive object={clonedScene} scale={0.3} />

      {/* Invisible hitbox */}
      <mesh visible={false}>
        <cylinderGeometry args={[0.8, 0.8, 2, 8]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>
    </group>
  );
}

export default function LocationCanvas({ agents, backgroundUrl, showDebugGeometry = false, walkableShape = 'square' }) {
  const sceneDebugGeometry = useWorldStore((s) => s.sceneDebugGeometry);
  const debugGeometryVisible = showDebugGeometry || sceneDebugGeometry;

  // Spread agents out so they don't overlap
  const startOffsets = agents.map((_, i) => {
    const count = agents.length;
    if (count === 1) return 0;
    const spread = Math.min(count * 1.5, WALK_X * 1.5);
    return ((i / (count - 1)) - 0.5) * spread;
  });

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {backgroundUrl && (
        <img
          src={backgroundUrl}
          alt=""
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center',
            zIndex: 0,
          }}
        />
      )}

      <Canvas
        camera={{
          position: [
            ROOM_SHELL_CAMERA_CONFIG.position.x,
            ROOM_SHELL_CAMERA_CONFIG.position.y,
            ROOM_SHELL_CAMERA_CONFIG.position.z,
          ],
          fov: ROOM_SHELL_CAMERA_CONFIG.fov,
          near: 0.1,
          far: 100,
        }}
        gl={{ alpha: true, antialias: true }}
        onCreated={({ camera }) => {
          camera.lookAt(
            ROOM_SHELL_CAMERA_CONFIG.lookAt.x,
            ROOM_SHELL_CAMERA_CONFIG.lookAt.y,
            ROOM_SHELL_CAMERA_CONFIG.lookAt.z
          );
        }}
        style={{
          position: 'absolute', inset: 0,
          zIndex: 1,
          background: backgroundUrl ? 'transparent' : '#06080c',
        }}
      >
        <ambientLight intensity={1.5} />
        <directionalLight
          position={[6, 10, 4]}
          intensity={2.4}
          color={0xfff4df}
          castShadow
        />
        <directionalLight
          position={[-4, 5, -6]}
          intensity={1.2}
          color={0x84a6ff}
        />
        <fog attach="fog" args={['#06080c', 18, 32]} />

        {agents.map((agent, i) => (
          <LocationCharacter
            key={agent.id}
            agentId={agent.id}
            modelGlb={getChatAppModelGlb(agent.profileDoc?.chat_app_model)}
            startX={startOffsets[i]}
          />
        ))}
        {debugGeometryVisible && <SceneDebugOverlay walkableShape={walkableShape} />}
      </Canvas>
    </div>
  );
}
